// 30-09 (Pau: «las imágenes que me envían los agentes se me abren en otra pestaña; ¿es difícil que se abra en la
// misma, como un pop-up?»). Un visor encima del chat, como en Telegram: la imagen en grande sobre el fondo
// oscurecido. Se cierra con la X, con Escape o pulsando fuera; «Abrir aparte» por si la quiere en otra pestaña.
import { ExternalLink, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { t } from '../../../i18n';

interface EstadoVisor {
  abierta: { src: string; nombre: string } | null;
  abrir: (src: string, nombre: string) => void;
  cerrar: () => void;
}

export const useVisor = create<EstadoVisor>((set) => ({
  abierta: null,
  abrir: (src, nombre) => set({ abierta: { src, nombre } }),
  cerrar: () => set({ abierta: null }),
}));

/** Una miniatura que abre el visor (con teclado también: es un botón). */
export function MiniaturaImagen({ src, nombre, alFallar }: { src: string; nombre: string; alFallar?: () => void }) {
  const abrir = useVisor((s) => s.abrir);
  return (
    <button type="button" onClick={() => abrir(src, nombre)} className="block w-full cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70" aria-label={t('oficinaV2.visor.ver', { nombre })}>
      <img src={src} alt={nombre} className="max-h-72 w-full object-cover" loading="lazy" onError={alFallar} />
    </button>
  );
}

export function VisorImagen() {
  const { abierta, cerrar } = useVisor();
  const boton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!abierta) return;
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar();
    };
    window.addEventListener('keydown', alTeclado);
    boton.current?.focus();
    return () => window.removeEventListener('keydown', alTeclado);
  }, [abierta, cerrar]);
  if (!abierta) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label={abierta.nombre} data-visor-imagen className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-6 animate-in fade-in-0" onClick={cerrar}>
      <img src={abierta.src} alt={abierta.nombre} className="max-h-[90vh] max-w-[92vw] rounded-o-s object-contain shadow-o-hoja" onClick={(e) => e.stopPropagation()} />
      <div className="absolute right-4 top-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <a href={abierta.src} target="_blank" rel="noreferrer" className="inline-flex h-10 items-center gap-2 rounded-full bg-white/10 px-4 text-o-texto text-white hover:bg-white/20">
          <ExternalLink className="h-4 w-4" aria-hidden />
          {t('oficinaV2.visor.abrirAparte')}
        </a>
        <button ref={boton} type="button" onClick={cerrar} aria-label={t('oficinaV2.visor.cerrar')} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white outline-none hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/70">
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>
    </div>
  );
}
